<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExternalLink, ClipboardPlus } from 'lucide-vue-next'
import BBadge from '@lib/components/base/BBadge.vue'
import type { Ticketset } from '@/composables/useTicketsetsData'

const props = defineProps<{
    ticketset: Ticketset
    expanded: boolean
}>()

const emit = defineEmits<{
    (e: 'toggle-expand'): void
    (e: 'view-tickets', id: number): void
    (e: 'add-to-clipboard'): void
}>()

const { t } = useI18n()

const pct = computed(() => {
    if (props.ticketset.ticketCount === 0) return 0
    return Math.round((props.ticketset.validatedCount / props.ticketset.ticketCount) * 100)
})

function onTicketsClick(ev: MouseEvent) {
    // Prevent the card's expand-toggle from firing alongside the link.
    ev.stopPropagation()
    emit('view-tickets', props.ticketset.id)
}

function onClipboardClick(ev: MouseEvent) {
    ev.stopPropagation()
    emit('add-to-clipboard')
}
</script>

<template>
    <article
        class="bg-white border border-gray-200 rounded-12 p-3 flex flex-col gap-2 transition"
        :class="{ 'border-teal': expanded }"
    >
        <div class="flex items-start gap-2">
            <button
                type="button"
                class="flex-1 min-w-0 text-left"
                @click="$emit('toggle-expand')"
            >
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-semibold text-ink truncate">{{ ticketset.name }}</span>
                    <BBadge
                        v-if="ticketset.bzucoId > 0"
                        variant="arch"
                        :label="t('ticketsets.bzucoSynced.badge')"
                    />
                </div>
                <div v-if="ticketset.customName" class="mt-0.5 text-xs text-gray-500 truncate">
                    {{ ticketset.customName }}
                </div>
                <div class="mt-0.5 text-sm text-gray-500 truncate">
                    {{ ticketset.event?.name ?? '—' }}
                </div>
                <div class="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
                    <span class="inline-flex items-center px-1.5 py-0.5 rounded-6 bg-gray-100 text-gray-600">
                        {{ ticketset.prices.length }}× {{ t('ticketsets.form.section.prices') }}
                    </span>
                    <span
                        v-if="ticketset.validate"
                        class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-6 bg-teal-l text-teal-d"
                    >
                        <span>✓</span><span>{{ t('ticketsets.list.cols.validate') }}</span>
                    </span>
                    <span class="text-gray-500 tabular-nums">
                        {{ ticketset.validatedCount }} / {{ ticketset.ticketCount }} ({{ pct }} %)
                    </span>
                </div>
            </button>
            <button
                type="button"
                class="text-gray-500 hover:text-teal transition-colors shrink-0 p-1"
                :title="t('ticketsets.qr.addToClipboard')"
                @click="onClipboardClick"
            >
                <ClipboardPlus class="w-4 h-4" />
            </button>
            <button
                type="button"
                class="text-gray-500 hover:text-teal transition-colors shrink-0 p-1"
                :title="t('ticketsets.list.viewTickets')"
                @click="onTicketsClick"
            >
                <ExternalLink class="w-4 h-4" />
            </button>
        </div>
    </article>
</template>
